import type { IconType } from '../../@types';
import type { Marketplace, SportIconVariant } from '../../@types/api';
import { capitalize, Box } from '@mui/material';
import { getMinioUrl } from '../../utils/envDynamicValues';
import { ICON_GROUP, SPORT_ICON_VARIANT } from '../../constants/enums';
import { useMarketplace } from '../../hooks/useMarketplace';
import { mergeSx } from '../../utils/styles';
import { CASINO_JACKPOT_ICON_NAMES, CasinoJackpotIconName } from '../../constants/icons';
import CasinoJackpotIcon from './CasinoJackpotIcon';

const DEFAULT_SPORT_ICON_VARIANT = SPORT_ICON_VARIANT.MULTICOLOR;

const MONOCHROME_SPORT_ICON_VARIANTS = new Set<SportIconVariant>([
  SPORT_ICON_VARIANT.SIMPLE,
  SPORT_ICON_VARIANT.NEOBET,
  SPORT_ICON_VARIANT.FLAT,
]);

const getIconExtension = (variant?: SportIconVariant) => {
  if (variant === SPORT_ICON_VARIANT.COLOR) {
    return 'webp';
  }

  return 'svg';
};

const getSportIconVariant = (sportsbookConfig?: Marketplace['sportsbookConfig']) =>
  sportsbookConfig?.assetVariants?.sportIcons ?? DEFAULT_SPORT_ICON_VARIANT;

type Props = {
  type?: IconType;
  name: string;
  small?: boolean;
};

const MinioIcon = ({ type = ICON_GROUP.SPORTS, name, small = false }: Props) => {
  const { config } = useMarketplace();
  const minioBaseUrl = getMinioUrl();

  if (!minioBaseUrl) return null;

  const isSportIcon = type === ICON_GROUP.SPORTS;
  const variant = isSportIcon ? getSportIconVariant(config?.sportsbookConfig) : undefined;
  const variantSegment = variant ? `/${variant}` : '';
  const shouldRenderBlack = variant ? MONOCHROME_SPORT_ICON_VARIANTS.has(variant) : false;
  const extension = getIconExtension(variant);

  const iconPath = `/resources/icons/${type}${variantSegment}/${name}.${extension}`;
  const iconUrl = `${minioBaseUrl}${iconPath}`;

  return (
    <Box
      component='img'
      src={iconUrl}
      alt={name}
      title={capitalize(name)}
      sx={mergeSx(
        { width: small ? '1rem' : '1.5rem', height: small ? '1rem' : '1.5rem' },
        shouldRenderBlack ? { filter: 'brightness(0)' } : undefined
      )}
    />
  );
};

const IconRenderer = ({ type = ICON_GROUP.SPORTS, name, small = false }: Props) => {
  if (!name) return null;

  if (type === ICON_GROUP.CASINO_JACKPOT) {
    const casinoIconName = name as CasinoJackpotIconName;

    return CASINO_JACKPOT_ICON_NAMES.includes(casinoIconName) ? <CasinoJackpotIcon name={casinoIconName} /> : null;
  }

  return <MinioIcon type={type} name={name} small={small} />;
};

export default IconRenderer;
